import { useState } from 'react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { MapProvider, useMap } from './context/MapContext';
import SiteHeader from './components/SiteHeader/SiteHeader';
import Sidebar from './components/Sidebar/Sidebar';
import MapView from './components/Map/MapView';
import AdminPage from './components/Admin/AdminPage';
import VLibras from './components/VLibras/VLibras';
import AccessibilityBar from './components/AccessibilityBar/AccessibilityBar';
import CookieBanner from './components/CookieBanner/CookieBanner';
import Footer from './components/Footer/Footer';
import PrivacyModal from './components/Modals/PrivacyModal';
import TermsModal from './components/Modals/TermsModal';
import LoginModal from './components/Modals/LoginModal';
import WelcomePopup from './components/WelcomePopup/WelcomePopup';
import TutorialPage from './pages/TutorialPage/TutorialPage';
import CatalogPage from './pages/CatalogPage/CatalogPage';
import SobrePage from './pages/SobrePage/SobrePage';
import FaleConoscoPage from './pages/FaleConoscoPage/FaleConoscoPage';
import SpotlightTour from './components/SpotlightTour/SpotlightTour';
import './App.css';

// MODO TEMPORÁRIO DE ATUALIZAÇÃO
// Para retornar o comportamento público antigo, troque para false.
// A estrutura original da aplicação foi preservada no componente PlatformContent,
// com os comentários existentes, para facilitar a reversão após a reunião de 10/07.
const MAINTENANCE_MODE = true;
const TEAM_ROLES = ['moderador', 'visualizador_interno', 'gestor_municipal', 'prestadora'];

function MaintenanceNotice({ onOpenLogin }) {
  const { user, logout } = useAuth();

  return (
    <main className="maintenance-page" id="main-content" tabIndex="-1">
      <section className="maintenance-panel" aria-labelledby="maintenance-title">
        <img
          src="/Logo_2.png"
          alt="Projeto Saneamento Vivo"
          className="maintenance-logo"
          width="210"
          height="105"
        />
        <p className="maintenance-kicker">Acesso temporariamente restrito</p>
        <h1 className="maintenance-title" id="maintenance-title">
          Estamos em processo de atualização
        </h1>
        <p className="maintenance-copy">
          A plataforma está passando por ajustes internos. O acesso ao painel está liberado apenas para a equipe autorizada neste momento.
        </p>
        <div className="maintenance-actions">
          <button className="maintenance-login-btn" type="button" onClick={onOpenLogin}>
            Entrar
          </button>
          {user && (
            <button className="maintenance-logout-btn" type="button" onClick={logout}>
              Sair de {user.name}
            </button>
          )}
        </div>
      </section>
    </main>
  );
}

function PlatformContent({
  currentPage,
  setCurrentPage,
  handleStartTour,
  setAdminOpen,
}) {
  return (
    <>
      <SiteHeader
        onAdminOpen={() => setAdminOpen(true)}
        onNavigate={setCurrentPage}
        currentPage={currentPage}
      />

      {/* id="main-content" — destino do skip link */}
      <div className={`app-content${currentPage !== 'home' ? ' app-content--page' : ''}`} id="main-content" tabIndex="-1">
        {currentPage === 'home' && (
          <>
            <Sidebar />
            <MapView />
          </>
        )}
        {currentPage === 'tutorial'     && <TutorialPage    onNavigate={setCurrentPage} onStartTour={handleStartTour} />}
        {currentPage === 'catalogo'     && <CatalogPage     onNavigate={setCurrentPage} />}
        {currentPage === 'sobre'        && <SobrePage       onNavigate={setCurrentPage} />}
        {currentPage === 'fale-conosco' && <FaleConoscoPage onNavigate={setCurrentPage} />}
      </div>
    </>
  );
}

function AppLayout() {
  const { theme, selectedMunicipality } = useMap();
  const { user, loading, hasRole } = useAuth();
  const [currentPage,      setCurrentPage]      = useState('home');
  const [activeTour,       setActiveTour]       = useState(null);
  const [adminOpen,        setAdminOpen]        = useState(false);
  const [welcomeDismissed, setWelcomeDismissed] = useState(false);
  const [loginOpen,        setLoginOpen]        = useState(false);
  const [contrast,    setContrast]    = useState(() => localStorage.getItem('a11y_contrast') === '1');
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [termsOpen,   setTermsOpen]   = useState(false);

  function handleStartTour(tourId) {
    setCurrentPage('home');
    setActiveTour(tourId);
  }

  function handleContrastToggle() {
    const next = !contrast;
    setContrast(next);
    localStorage.setItem('a11y_contrast', next ? '1' : '0');
  }

  const canAccessPlatform = !MAINTENANCE_MODE || (user && TEAM_ROLES.includes(user.role));

  if (adminOpen && hasRole('moderador')) {
    return <AdminPage theme={theme} onClose={() => setAdminOpen(false)} />;
  }

  const rootClass = [
    theme === 'dark' ? 'theme-dark' : 'theme-light',
    contrast ? 'theme-contrast' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={`app-root ${rootClass}`}>
      {/* Skip link — visível ao foco por teclado (LBI/E-MAG) */}
      <a href="#main-content" className="skip-link">Ir para o conteúdo principal</a>

      {/* Barra de acessibilidade — E-MAG / Decreto 10.645/2021 */}
      <AccessibilityBar contrast={contrast} onContrastToggle={handleContrastToggle} />

      {MAINTENANCE_MODE && loading ? (
        <main className="maintenance-page" id="main-content" tabIndex="-1">
          <div className="maintenance-loading">Carregando...</div>
        </main>
      ) : canAccessPlatform ? (
        <PlatformContent
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          handleStartTour={handleStartTour}
          setAdminOpen={setAdminOpen}
        />
      ) : (
        <MaintenanceNotice onOpenLogin={() => setLoginOpen(true)} />
      )}

      {/* Rodapé com links legais */}
      <Footer
        onPrivacy={() => setPrivacyOpen(true)}
        onTerms={()   => setTermsOpen(true)}
      />

      {/* Banner LGPD — aparece apenas na primeira visita */}
      <CookieBanner onPrivacy={() => setPrivacyOpen(true)} />

      {/* Modais legais */}
      {privacyOpen && <PrivacyModal onClose={() => setPrivacyOpen(false)} />}
      {termsOpen   && <TermsModal   onClose={() => setTermsOpen(false)} />}

      {/* Login modal — pode ser aberto pelo WelcomePopup ou pelo SiteHeader */}
      {loginOpen && <LoginModal onClose={() => setLoginOpen(false)} />}

      {/* Wizard PMSB (Guto) — aparece enquanto nenhum município selecionado */}
      {canAccessPlatform && !selectedMunicipality && !welcomeDismissed && (
        <WelcomePopup
          onDismiss={() => setWelcomeDismissed(true)}
          onOpenLogin={() => setLoginOpen(true)}
        />
      )}

      {/* Tour guiado — overlay sobre a interface real */}
      {canAccessPlatform && activeTour && (
        <SpotlightTour
          tourId={activeTour}
          onClose={() => setActiveTour(null)}
        />
      )}
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <MapProvider>
        <AppLayout />
        <VLibras />
      </MapProvider>
    </AuthProvider>
  );
}

export default App;
